<template>
  <v-app style="width: 100vw;">
    <v-main>
      <!-- Sticky Navbar -->
      <v-app-bar class="nav-bar" fixed>
        <v-toolbar-title>
          <img :src="navLogo" alt="Simple Serve Software" class="nav-logo" />
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <template v-if="isMobile">
          <v-app-bar-nav-icon @click="drawer = !drawer"></v-app-bar-nav-icon>
        </template>
        <template v-else>
          <v-btn text @click="scrollTo('hero')">{{ $t('home') }}</v-btn>
          <v-btn text @click="scrollTo('caas')">{{ $t('caas') }}</v-btn>
          <v-btn text @click="scrollTo('custom-development')">{{ $t('customDevelopment') }}</v-btn>
          <v-btn icon @click="setLanguage('en')">
            <span class="fi fi-us"></span>
          </v-btn>
          <v-btn icon @click="setLanguage('es')">
            <span class="fi fi-es"></span>
          </v-btn>
        </template>
      </v-app-bar>

      <v-navigation-drawer v-model="drawer" app temporary>
        <v-list>
          <v-list-item @click="scrollTo('hero')">{{ $t('home') }}</v-list-item>
          <v-list-item @click="scrollTo('caas')">{{ $t('caas') }}</v-list-item>
          <v-list-item @click="scrollTo('custom-development')">{{ $t('customDevelopment') }}</v-list-item>
          <v-list-item @click="setLanguage('en')">
            <span class="fi fi-us"></span> English
          </v-list-item>
          <v-list-item @click="setLanguage('es')">
            <span class="fi fi-es"></span> Spanish
          </v-list-item>
        </v-list>
      </v-navigation-drawer>

      <!-- Hero Section -->
      <div class="hero-container">
        <div class="hero-image">
          <div :class="['contentArea', { 'contentArea--mobile': isMobile }]">
            <div :class="['hero-text', { 'mobile-hero-text': isMobile }]">
              {{ $t('heroText') }}
              <br />
              <span class="emphasize">{{ $t('heroEmphasize') }}</span>
            </div>
            <div class="hero-cta">
              <v-btn>
                <v-icon>mdi-arrow-down</v-icon>
              </v-btn>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="page-container">
        <!-- CaaS Section -->
        <div class="section">
          <div :class="['contentArea', { 'contentArea--mobile': isMobile }]">
            <div id="caas">
              <v-card class="section-card">
                <v-card-title :class="{ 'mobile-card-title': isMobile }">{{ $t('ctaAsServiceTitle') }}</v-card-title>
                <p :class="{ 'mobile-card-subheading': isMobile }">{{ $t('ctaAsServiceSubheading') }}</p>
                <v-card-text style="padding: 0px;">
                  <p :class="{ 'mobile-card-text': isMobile }">{{ $t('ctaAsServiceText') }}</p>
                  <caasPlans />
                </v-card-text>
              </v-card>
            </div>
          </div>
        </div>

        <!-- Custom Development Section -->
        <div class="section">
          <div :class="['contentArea', { 'contentArea--mobile': isMobile }]">
            <div id="custom-development">
              <v-card class="section-card">
                <v-card-title :class="{ 'mobile-card-title': isMobile }">{{ $t('customDevelopmentTitle') }}</v-card-title>
                <p :class="{ 'mobile-card-subheading': isMobile }">{{ $t('customDevelopmentSubheading') }}</p>
                <v-card-text>
                  <p :class="{ 'mobile-card-text': isMobile }">{{ $t('customDevelopmentText') }}</p>

                  <div class="book-call-wrapper">
                    <v-btn class="book-call-btn" @click="openCalendly('senior')" prepend-icon="mdi-calendar-clock">
                      Book a Call
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </div>
        </div>

        <!-- AI Chatbot Section -->
        <div class="section">
          <div :class="['contentArea', { 'contentArea--mobile': isMobile }]">
            <AiChatbots :isMobile="isMobile" />
          </div>
        </div>

        <!-- Customer Reviews Section -->
        <div class="section">
          <div :class="['contentArea', { 'contentArea--mobile': isMobile }]">
            <div id="customer-reviews">
              <v-card class="section-card">
                <v-card-title class="card-title">{{ $t('customerReviewsTitle') }}</v-card-title>
                <p class="card-subheading">{{ $t('customerReviewsSubheading') }}</p>
                <v-card-text style="padding: 0px;">
                  <p class="card-text">{{ $t('customerReviewsText') }}</p>
                  <CustomerReviews :isMobile="isMobile" />
                </v-card-text>
              </v-card>
            </div>
          </div>
        </div>

        <!-- Why Choose Us Section -->
        <div class="section">
          <div :class="['contentArea', { 'contentArea--mobile': isMobile }]">
            <WhyChooseUs :isMobile="isMobile" />
          </div>
        </div>
      </div>

      <!-- Footer -->
      <SiteFooter :isMobile="isMobile" @scroll="scrollTo" />
    </v-main>
  </v-app>
</template>

<script>
import { ref, onMounted, onUnmounted } from "vue";
import { useI18n } from 'vue-i18n';
import caasPlans from "./components/caasPlans.vue";
import CustomerReviews from "./components/CustomerReviews.vue";
import AiChatbots from "./components/AiChatbots.vue";
import WhyChooseUs from "./components/WhyChooseUs.vue";
import SiteFooter from "./components/SiteFooter.vue";
import navLogo from "./assets/logo-horizontal-dark.svg";

export default {
  name: "App",
  components: {
    caasPlans,
    CustomerReviews,
    AiChatbots,
    WhyChooseUs,
    SiteFooter,
  },
  setup() {
    const isMobile = ref(window.innerWidth <= 768 || window.matchMedia("(display-mode: standalone)").matches);
    const updateIsMobile = () => {
      isMobile.value = window.innerWidth <= 768 || window.matchMedia("(display-mode: standalone)").matches;
    };

    const drawer = ref(false);

    const { locale } = useI18n();
    const setLanguage = (lang) => {
      locale.value = lang;
      localStorage.setItem('language', lang);
    };

    const scrollTo = (sectionId) => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    };

    const openCalendly = (type) => {
      const url =
        type === "senior"
          ? "https://calendly.com/ss-sbs-info/30min"
          : "https://calendly.com/ss-sbs-info/30min";
      window.open(url, '_blank');
      return false;
    };

    onMounted(() => {
      window.addEventListener("resize", updateIsMobile);
      updateIsMobile();
    });

    onUnmounted(() => {
      window.removeEventListener("resize", updateIsMobile);
    });

    return {
      drawer,
      isMobile,
      scrollTo,
      openCalendly,
      setLanguage,
      navLogo,
    };
  },
};
</script>

<style scoped>
.contentArea {
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
  padding-left: 3rem;
  padding-right: 3rem;
  flex: 1;
}

.contentArea--mobile {
  padding-left: 1rem;
  padding-right: 1rem;
  max-width: 100%;
}

.nav-bar {
  background-color: #000000 !important;
  box-shadow: 10px !important;
  color: white !important;
}

.hero-container {
  padding: 0;
  background: linear-gradient(90deg, #000000, #3533cd);
  color: white;
}

.hero-image {
  background: radial-gradient(circle 450px at center, #3533cd, #000000);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
}

.hero-text {
  font-family: "Courier New", Courier, monospace;
  font-size: 52px;
  font-variant-ligatures: none;
  text-align: center;
  font-weight: 2000;
  color: #ffffff;
}

.emphasize {
  background-color: #ffffff;
  animation: emphasizeAnimation 1s ease-out;
  font-weight: bold;
  color: black;
}

@keyframes emphasizeAnimation {
  from {
    background-color: transparent;
    transform: scale(1);
  }
  to {
    background-color: #ffffff;
    transform: scale(1.2);
  }
}

.hero-cta {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 2rem;
  color: white;
  margin-top: 2rem;
}

.section {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 0;
}

.page-container {
  background: linear-gradient(180deg, #000000, #3533cd) !important;
}

.section-card {
  margin: 10px !important;
  padding: 20px !important;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2) !important;
  background: linear-gradient(90deg, #03000f, #3533cd) !important;
  color: #ffffff !important;
  border-radius: 25px !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
}

.nav-logo {
  height: 40px;
  display: block;
}

@media (max-width: 768px) {
  .nav-logo {
    height: 32px;
  }
}

.book-call-wrapper {
  margin-top: 1.5rem;
}

.book-call-btn {
  background: #f3d129 !important;
  color: #03000f !important;
  font-weight: 700 !important;
  font-size: 1rem !important;
  height: 48px !important;
  padding: 0 2rem !important;
  border-radius: 10px !important;
  letter-spacing: 0.3px !important;
  box-shadow: 0 4px 16px rgba(243, 209, 41, 0.25) !important;
}

.card-title {
  font-size: 2rem;
  font-weight: 800;
  color: #ffffff;
}

.card-subheading {
  color: rgba(255, 255, 255, 0.85);
  font-size: 1.2rem;
  margin-bottom: 20px;
}

.card-text {
  font-size: 1.1rem;
  line-height: 1.6;
  color: #ffffff;
}

.fi {
  margin-right: 8px;
}

.mobile-hero-text {
  font-size: 32px;
}

.mobile-card-title {
  font-size: 1.5rem;
}

.mobile-card-subheading {
  font-size: 1rem;
}

.mobile-card-text {
  font-size: 0.9rem;
}
</style>
